import { LoadingOutlined } from "@ant-design/icons"
import { Spin, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import Button from "../../../../../Components/Button"
import Spinner from "../../../../../Components/Spinner"
import { RoutesPaths } from "../../../../../constants/user-routes"
import Icon from "../../../../../Icon"
import { highAccessRoles } from "../../../../../Utils/permission"
import { CE_PAGE_STATE } from "../../constants/page-states"
import { selectCompellingEvidence } from "../../redux/compelling-evidence.slice"
import CaseGhostTable from "../CaseGhostTable"

import styles from "./styles.module.less"

const { Title, Text } = Typography

export const CaseStateOverlay = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const baRoleId = useSelector((s) => s.auth?.userInfo?.ba_role?.id)
  const { pageState, hasIntegration, columns } = useSelector(selectCompellingEvidence)

  const isPrivileged = highAccessRoles.includes(baRoleId)

  // Role first: a Manager can't enroll whatever the integrations are.
  const enrollBlockedReason =
    (!isPrivileged && t("Allows for Owner or Administrator roles")) ||
    (!hasIntegration && t("Connect Shopify or Stripe to enroll.")) ||
    ""

  const states = {
    [CE_PAGE_STATE.NOT_ENROLLED]: {
      icon: <Icon type="receipt_bill" style={{ fontSize: 50 }} />,
      title: t("Enroll in Compelling Evidence and First-Party Trust to access this feature"),
      description: t(
        "After turning on, data will not appear immediately. It usually takes up to 7 days for the first fraud disputes to show up here."
      ),
      action: (
        <Tooltip title={enrollBlockedReason}>
          <Button
            type="primary"
            onClick={() => navigate(RoutesPaths.receipts.configure_business)}
            disabled={!!enrollBlockedReason}
            data-testid="deflect-ce-enroll-btn"
          >
            {t("Enroll now")}
          </Button>
        </Tooltip>
      )
    },
    [CE_PAGE_STATE.PENDING]: {
      icon: <Icon type="receipt_bill" style={{ fontSize: 50 }} />,
      title: t("Enrollment request submitted"),
      description: t(
        "Our team is reviewing your request and will submit it on your behalf. We'll email you once enrollment is confirmed. The first fraud disputes usually appear within 7 days after that."
      ),
      action: (
        <span className={styles.pill}>
          <Spin indicator={<LoadingOutlined spin className={styles.pillSpinner} />} size="small" />
          {t("Pending...")}
        </span>
      )
    },
    [CE_PAGE_STATE.NO_INTEGRATION]: {
      icon: <Icon type="question_help" style={{ fontSize: 50 }} />,
      title: t("Connect an integration to keep Compelling Evidence working"),
      description: t(
        "Shopify and Stripe are disconnected. Without your order data (IP address, device, delivery address) we can't prove a purchase was real, so new fraud disputes aren't blocked."
      ),
      action: (
        <Button
          type="primary"
          onClick={() => navigate(RoutesPaths.configure.processors)}
          data-testid="deflect-ce-connect-integration-btn"
        >
          {t("Connect integration")}
        </Button>
      )
    }
  }

  const state = states[pageState]

  return (
    <div className={styles.wrap} data-testid={`deflect-ce-${pageState}`}>
      <CaseGhostTable activeColumns={columns} />
      <div className={styles.overlay}>
        {state ? (
          <div className={styles.state}>
            <span className={styles.icon}>{state.icon}</span>
            <Title level={3} className={styles.title} data-testid={`deflect-ce-${pageState}-title`}>
              {state.title}
            </Title>
            <Text
              type="secondary"
              className={styles.text}
              data-testid={`deflect-ce-${pageState}-description`}
            >
              {state.description}
            </Text>
            <div className={styles.action}>{state.action}</div>
          </div>
        ) : (
          <div className={styles.loading}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}
